import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  TableRow,
  TableCell,
  Paper,
  Table,
  TableContainer,
  TableHead,
  Typography,
  useTheme,
  TableBody,
  Box
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { ServiceData } from "../ScheduleSelection-Tab-Services.component";
import { renderRoomLayoutCell } from "../../../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/RoomLayoutDialogContent/RoomLayoutTable.partial";
import { renderServiceImage } from "../../../../FacilityManager/Components/Toolbars/InfoSidebar/ZoneInfoSidebar/ServicePartial/ServiceCommonRender.partial";
import { EnlargedImageDialog } from "../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { ServiceDeliveryStatus } from "../../../../../features/Booking-Form/typings/booking-service-administration.types";
import { sortDataByOrdered } from "../functions/sortDataByOrdered.function";
import { BookingRequestsStatus } from "../../../../BookingRequest/typings/booking-requests.types";

export interface ServiceRoomLayoutData extends ServiceData {
  name: string | undefined;
  arrangement: boolean[][] | undefined;
}

type P = {
  isEditable: boolean;
  layoutData: ServiceRoomLayoutData[];
  setLayoutData: Dispatch<SetStateAction<ServiceData[]>>;
};

export function ServiceRoomLayoutDataTable({ isEditable, layoutData, setLayoutData }: Readonly<P>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const isServiceEditable = isEditable;

  const [entries, setEntries] = useState<ServiceRoomLayoutData[]>([]);
  const [selectedLayout, setSelectedLayout] = useState<number | null>(null);
  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const handleChange = (event: React.MouseEvent<HTMLElement>, newLayout: number) => {
    setSelectedLayout(newLayout);
    const updated = layoutData.map(item => {
      const isSelected = item.id === newLayout;

      return {
        ...item,
        ordered: isSelected,
        status: isSelected ? BookingRequestsStatus.REQUESTED : null,
        deliveryStatus: isSelected ? ServiceDeliveryStatus.ORDERED : null
      };
    });

    setLayoutData(sortDataByOrdered(updated));
    setEntries(sortDataByOrdered(updated));
  };

  useEffect(() => {
    if (layoutData.length) {
      const orderedId = layoutData.find(layout => layout.ordered)?.id;
      setSelectedLayout(orderedId ?? null);
      setEntries(sortDataByOrdered(layoutData));
    }
  }, []);

  return (
    <>
      <TableContainer component={Paper}>
        <Table aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>{t("Layout")}</TableCell>
              <TableCell align="left">{t("Image")}</TableCell>
              <TableCell align="left">{t("Name")}</TableCell>
              <TableCell align="left">{t("Status")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            <>
              {!entries.length && (
                <Box sx={{ pl: 2 }}>
                  <Typography variant={"body2"} sx={{ visibility: "visible" }}>
                    {t("There are no available room layout")}
                  </Typography>
                </Box>
              )}

              {entries.map(item => (
                <TableRow
                  key={item.id}
                  hover
                  selected={selectedLayout === item.id}
                  data-testid={`toggle-layout-row-${item.id}`}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    cursor: isServiceEditable ? "pointer" : "default"
                  }}
                  onClick={e => {
                    if (isServiceEditable) handleChange(e, item.id);
                  }}
                >
                  <TableCell scope="row" width={150}>
                    {renderRoomLayoutCell(item.arrangement ?? [], theme)}
                  </TableCell>
                  <TableCell scope="row" width={150}>
                    {renderServiceImage(
                      80,
                      item.picturePaths ? item.picturePaths[item.picturePaths.length - 1] : null,
                      setShowBiggerImage
                    )}
                  </TableCell>

                  <TableCell align="left" width={160}>
                    <Typography sx={{ width: 160 }}>{t(item.name ?? "Layout")}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{t(item.status ?? "")}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </>
          </TableBody>
        </Table>
      </TableContainer>

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
